import { ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';
import { DevicesStore, InstallationFeaturesStore } from '@store';
import {
  ActivateDeactivateCommands,
  DeviceIds,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  RoomsStatusProperties,
  SrcCondensateAvoidanceCommands,
  SrcCondensateAvoidanceStatusProperties,
  SrcPumpControlStatusProperties,
} from '@vi-iot/client/index';
import { forkJoin, Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { Bool } from 'src/app/shared/common/enums';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-src-settings-algorithms',
  templateUrl: './src-settings-algorithms.component.html',
  styleUrls: ['./src-settings-algorithms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcSettingsAlgorithmsComponent implements OnInit {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  isSupplyChannelToggleAvailable: boolean;

  pumpControlIds = ['0', '1', '2', '3'];

  listPumpControlFeatures: Feature<
    SrcPumpControlStatusProperties,
    ActivateDeactivateCommands
  >[] = [];

  status$: Observable<Feature<RoomsStatusProperties, ActivateDeactivateCommands>>;
  condensateFeatures$: Observable<
    Feature<SrcCondensateAvoidanceStatusProperties, SrcCondensateAvoidanceCommands>
  >;
  featurePropertyKey = FeaturePropertyKey;
  bool = Bool;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    protected installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore,
    protected translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomStatus,
      DeviceIds.RoomControl
    );

    const pumpControlFeatures = this.pumpControlIds.map((pumpId) =>
      this.getSrcChannelPumpControl(pumpId).pipe(
        take(1),
        tap((feature) => {
          if (feature) {
            this.listPumpControlFeatures.push(feature);
          }
        })
      )
    );

    this.condensateFeatures$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      SrcCondensateAvoidanceStatusProperties,
      SrcCondensateAvoidanceCommands
    >(FeatureNames.condensateAvoidance, DeviceIds.RoomControl);

    forkJoin(pumpControlFeatures)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        this.isSupplyChannelToggleAvailable = this.listPumpControlFeatures.length > 0;
      });
  }

  getSrcChannelPumpControl(
    pumpId: string
  ): Observable<Feature<SrcPumpControlStatusProperties, ActivateDeactivateCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      SrcPumpControlStatusProperties,
      ActivateDeactivateCommands
    >(FeatureNames.srcChannelPumpControl(pumpId), DeviceIds.RoomControl);
  }
}
